/**
 * modes.ts
 *
 * Display metadata for Workspace Modes and Features. Pure data and helpers so
 * the shell components stay presentational.
 *
 * Requirements: 3.1, 3.2, 3.5.
 */

import { JobType } from '../graphql/types';
import { WORKSPACE_MODES, type WorkspaceMode } from './urlState';

export const MODE_LABELS: Readonly<Record<WorkspaceMode, string>> = {
  intake: 'Intake',
  extract: 'Extract',
  analyze: 'Analyze',
  review: 'Review',
  findings: 'Findings',
  analysis: 'Title analysis',
};

export interface ModeOption {
  id: WorkspaceMode;
  label: string;
}

/**
 * Locked Modes mapped to the reason shown in the segment's `title`.
 * A Mode is locked when it has an entry here (even with an empty reason).
 */
export type LockedModes = Partial<Record<WorkspaceMode, string>>;

/** Mode_Switch options for a Feature, in display order. */
export function getModeOptions(feature: JobType): ModeOption[] {
  return WORKSPACE_MODES[feature].map((id) => ({ id, label: MODE_LABELS[id] }));
}

/** True when `mode` has an entry in `locked`. */
export function isModeLocked(locked: LockedModes | undefined, mode: WorkspaceMode): boolean {
  return !!locked && Object.prototype.hasOwnProperty.call(locked, mode);
}

/** Feature home route, used by the Top_Bar back link. */
export const FEATURE_HOME_PATH: Readonly<Record<JobType, string>> = {
  [JobType.DD]: '/due-diligence',
  [JobType.BA]: '/borrower-analysis',
  [JobType.TR]: '/title-search',
};

/** Matches `/due-diligence/:jobId`, `/borrower-analysis/:jobId`, `/title-search/:jobId`. */
const WORKSPACE_PATH = /^\/(?:due-diligence|borrower-analysis|title-search)\/[^/]+\/?$/;

/**
 * True for Workspace routes. These render full-bleed, without the Feature
 * sidebar layout (Requirement 3.1).
 */
export function isWorkspacePath(pathname: string): boolean {
  return WORKSPACE_PATH.test(pathname);
}

/** Two-letter initials from a display name, or a single letter. */
export function getInitials(name: string | undefined | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}
